import {
  SvgType,
  SymbolType,
  isSvgType,
} from "../../../../types/floorplannerTypes";
import {
  Product3DAssetBounds,
  Product3DAssetCompression,
  Product3DAssetReference,
  Product3DDimensions,
} from "../../products/product3DTypes";
import {
  PrimitiveSymbol3DModelKey,
  SYMBOL_3D_MODEL_YAW_OFFSETS,
  getPrimitiveSymbol3DDefaults,
  getPrimitiveSymbol3DModelKey,
  getPrimitiveSymbol3DModelKeyForSymbol,
} from "../registry/primitiveSymbol3DConfig";
import {
  ReadyStandardSymbol3DAsset,
  STANDARD_SYMBOL_3D_ASSETS,
  StandardSymbol3DAsset,
  getReadyStandardSymbol3DAsset,
} from "./standardSymbol3DAssets";
import { dimensionsFromAssetBounds } from "./symbol3DAssetTransform";
import { resolveUserModel3DUrl } from "../../userModels/userModel3DUrls";

export type ResolvedSymbol3DAssetSource = "userModel" | "product" | "standard";

export type ResolvedSymbol3DAsset = {
  source: ResolvedSymbol3DAssetSource;
  cacheKey: string;
  url: string;
  id?: string;
  version?: string;
  modelKey?: PrimitiveSymbol3DModelKey;
  dimensions: Product3DDimensions;
  bounds?: Product3DAssetBounds;
  compression?: Product3DAssetCompression;
  pitchOffset?: number;
  rollOffset?: number;
  yawOffset?: number;
};

export type Symbol3DAssetResolution =
  | {
      mode: "asset3D";
      asset: ResolvedSymbol3DAsset;
    }
  | {
      mode: "billboardPreview";
      reason: string;
    }
  | {
      mode: "primitiveFallback";
      reason: string;
      modelKey?: PrimitiveSymbol3DModelKey;
    };

export type Symbol3DAssetResolverOptions = {
  assetsEnabled?: boolean;
  standardAssets?: Record<PrimitiveSymbol3DModelKey, StandardSymbol3DAsset>;
};

export function resolveSymbol3DAsset(
  symbol: SymbolType,
  options: Symbol3DAssetResolverOptions = {},
): Symbol3DAssetResolution {
  const assetsEnabled = options.assetsEnabled ?? true;
  const userModel = symbol.threeD?.userModel;
  const product = symbol.threeD?.product;
  const modelKey = getSymbolOrProductProxyModelKey(symbol);

  if (symbol.threeD?.sourceMode === "primitiveOnly") {
    return {
      mode: "primitiveFallback",
      reason: "Primitive-only mode is selected for this symbol.",
      modelKey,
    };
  }

  if (userModel?.status === "ready" && userModel.url) {
    return {
      mode: "asset3D",
      asset: resolveUserModelAsset(symbol, userModel),
    };
  }

  if (!assetsEnabled) {
    if (
      product?.renderMode === "billboardPreview" &&
      product.selectedImageUrl
    ) {
      return {
        mode: "billboardPreview",
        reason: "Catalog product is configured to use image preview.",
      };
    }
    return {
      mode: "primitiveFallback",
      reason: "3D asset rendering is disabled.",
      modelKey,
    };
  }

  if (product?.renderMode === "asset3D") {
    const productAsset = resolveReadyProductAsset(symbol, product.asset);
    if (productAsset) {
      return { mode: "asset3D", asset: productAsset };
    }
  }

  const standardAsset = getReadyStandardSymbol3DAsset(
    modelKey,
    options.standardAssets ?? STANDARD_SYMBOL_3D_ASSETS,
  );
  if (standardAsset) {
    return {
      mode: "asset3D",
      asset: resolveStandardAsset(symbol, standardAsset),
    };
  }


  if (product?.renderMode === "billboardPreview" && product.selectedImageUrl) {
    return {
      mode: "billboardPreview",
      reason: "Catalog product is configured to use image preview.",
    };
  }

  return {
    mode: "primitiveFallback",
    reason: standardAsset
      ? "Standard 3D asset is unavailable."
      : "No ready standard 3D asset exists for this symbol.",
    modelKey,
  };
}

export function getSymbolOrProductProxyModelKey(
  symbol: SymbolType,
): PrimitiveSymbol3DModelKey | undefined {
  const proxyModelKey = getPrimitiveSymbol3DModelKey(
    symbol.threeD?.product?.proxyModelKey,
  );
  return proxyModelKey ?? getPrimitiveSymbol3DModelKeyForSymbol(symbol);
}

export function getSymbol3DAssetTargetDimensions(
  symbol: SymbolType,
  assetDimensions?: Product3DDimensions,
  assetBounds?: Product3DAssetBounds,
): Product3DDimensions {
  const modelKey = getSymbolOrProductProxyModelKey(symbol);
  const defaults = getPrimitiveSymbol3DDefaults(modelKey)?.dimensions;
  const boundsDimensions = dimensionsFromAssetBounds(assetBounds);
  const productDimensions = symbol.threeD?.product?.dimensions;
  const stableUserModelDimensions = tiltsUploadedUserModel(symbol)
    ? {
        width:
          symbol.threeD?.userModel?.dimensions?.width ??
          symbol.threeD?.dimensions?.width,
        depth:
          symbol.threeD?.userModel?.dimensions?.depth ??
          symbol.threeD?.dimensions?.depth,
      }
    : undefined;
  return {
    width:
      stableUserModelDimensions?.width ??
      getSymbolWidth(symbol) ??
      symbol.threeD?.dimensions?.width ??
      productDimensions?.width ??
      assetDimensions?.width ??
      boundsDimensions?.width ??
      defaults?.width,
    depth:
      stableUserModelDimensions?.depth ??
      getSymbolDepth(symbol) ??
      symbol.threeD?.dimensions?.depth ??
      productDimensions?.depth ??
      assetDimensions?.depth ??
      boundsDimensions?.depth ??
      defaults?.depth,
    height:
      symbol.threeD?.height ??
      symbol.threeD?.dimensions?.height ??
      productDimensions?.height ??
      assetDimensions?.height ??
      boundsDimensions?.height ??
      defaults?.height,
  };
}

function tiltsUploadedUserModel(symbol: SymbolType): boolean {
  if (!symbol.threeD?.userModel) return false;
  const rotation = symbol.threeD.rotation3D;
  const epsilon = 1e-6;
  return (
    Math.abs(rotation?.pitch ?? 0) > epsilon ||
    Math.abs(rotation?.roll ?? 0) > epsilon
  );
}

function resolveReadyProductAsset(
  symbol: SymbolType,
  asset: Product3DAssetReference | undefined,
): ResolvedSymbol3DAsset | undefined {
  if (asset?.status !== "ready" || !asset.glbUrl) return undefined;
  const pitchOffset = getSymbolModelPitchOffset(symbol);
  const rollOffset = getSymbolModelRollOffset(symbol);
  const yawOffset = getSymbolModelYawOffset(symbol);
  return {
    source: "product",
    cacheKey: `product:${asset.id}:${asset.quality?.sha256 ?? asset.glbUrl}:${
      pitchOffset ?? 0
    }:${rollOffset ?? 0}:${yawOffset ?? 0}`,
    id: asset.id,
    url: resolveUserModel3DUrl(asset.glbUrl) ?? asset.glbUrl,
    version:
      typeof asset.quality?.pipelineVersion === "string"
        ? asset.quality.pipelineVersion
        : undefined,
    pitchOffset,
    rollOffset,
    yawOffset,
    dimensions: getSymbol3DAssetTargetDimensions(
      symbol,
      asset.dimensions,
      asset.bounds,
    ),
    bounds: asset.bounds,
    compression: asset.quality?.compression,
  };
}

/** Asset correction for models authored on axes that do not match the symbol. */
function getSymbolModelYawOffset(symbol: SymbolType): number | undefined {
  const configured = symbol.threeD?.modelYawOffset;
  const yawOffset =
    typeof configured === "number" && Number.isFinite(configured)
      ? configured
      : SYMBOL_3D_MODEL_YAW_OFFSETS[
          getSymbolOrProductProxyModelKey(symbol) ??
            ("" as PrimitiveSymbol3DModelKey)
        ];
  return normalizeRotationOffset(yawOffset);
}

function getSymbolModelPitchOffset(symbol: SymbolType): number | undefined {
  return normalizeRotationOffset(symbol.threeD?.modelPitchOffset);
}

function getSymbolModelRollOffset(symbol: SymbolType): number | undefined {
  return normalizeRotationOffset(symbol.threeD?.modelRollOffset);
}

function normalizeRotationOffset(value: number | undefined): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value !== 0
    ? value
    : undefined;
}

function resolveStandardAsset(
  symbol: SymbolType,
  asset: ReadyStandardSymbol3DAsset,
): ResolvedSymbol3DAsset {
  const pitchOffset = getSymbolModelPitchOffset(symbol);
  const rollOffset = getSymbolModelRollOffset(symbol);
  const yawOffset = getSymbolModelYawOffset(symbol);
  return {
    source: "standard",
    cacheKey: `standard:${asset.modelKey}:${asset.version}:${asset.url}:${
      pitchOffset ?? 0
    }:${rollOffset ?? 0}:${yawOffset ?? 0}`,
    id: asset.modelKey,
    modelKey: asset.modelKey,
    url: asset.url,
    version: asset.version,
    pitchOffset,
    rollOffset,
    yawOffset,
    dimensions: getSymbol3DAssetTargetDimensions(
      symbol,
      asset.dimensions,
      asset.bounds,
    ),
    bounds: asset.bounds,
    compression: asset.compression,
  };
}

function resolveUserModelAsset(
  symbol: SymbolType,
  userModel: NonNullable<NonNullable<SymbolType["threeD"]>["userModel"]>,
): ResolvedSymbol3DAsset {
  const url = resolveUserModelUrl(userModel.url, userModel.modelId);
  // v2-normalized models always include bounds. If that metadata is missing,
  // keep the legacy upright correction instead of trusting a stale version flag.
  const resolvedVersion =
    userModel.version === "user-models-v2" && !userModel.bounds
      ? "user-models-v1"
      : (userModel.version ??
        (userModel.bounds ? "user-models-v2" : "user-models-v1"));
  const pitchOffset = resolveUserModelRotationOffset(
    userModel.pitchOffset,
    getSymbolModelPitchOffset(symbol),
  );
  const rollOffset = resolveUserModelRotationOffset(
    userModel.rollOffset,
    getSymbolModelRollOffset(symbol),
  );
  const yawOffset = resolveUserModelRotationOffset(
    userModel.yawOffset,
    getSymbolModelYawOffset(symbol),
  );
  return {
    source: "userModel",
    cacheKey: `user:${userModel.modelId}:${
      userModel.sha256 ?? userModel.version ?? url
    }`,
    id: userModel.modelId,
    url,
    version: resolvedVersion,
    pitchOffset,
    rollOffset,
    yawOffset,
    dimensions: getSymbol3DAssetTargetDimensions(
      symbol,
      userModel.dimensions,
      userModel.bounds,
    ),
    bounds: userModel.bounds,
  };
}

function resolveUserModelRotationOffset(
  userModelValue: number | undefined,
  symbolValue: number | undefined,
): number | undefined {
  if (typeof userModelValue === "number" && Number.isFinite(userModelValue)) {
    return normalizeRotationOffset(userModelValue);
  }
  return symbolValue;
}

function resolveUserModelUrl(url: string, modelId: string): string {
  const serverUri = process.env.REACT_APP_SERVER_URI;
  const backendPath = `/user-models-3d/${modelId}/optimized.glb`;
  if (
    serverUri &&
    /^https?:\/\//i.test(url) &&
    /\/user-models\/[^/]+\/[^/]+\/optimized\.glb(?:$|\?)/i.test(url)
  ) {
    return resolveUserModel3DUrl(backendPath) ?? backendPath;
  }
  if (/^https?:\/\//i.test(url)) return url;
  return resolveUserModel3DUrl(url) ?? url;
}

function getSymbolWidth(symbol: SymbolType): number | undefined {
  return isSvgType(symbol) ? (symbol as SvgType).svgLength : undefined;
}

function getSymbolDepth(symbol: SymbolType): number | undefined {
  return isSvgType(symbol) ? (symbol as SvgType).svgHeight : undefined;
}
